/* Reports → Audit log (designs/intake-input-path.md IP7): every effort's
   filing and the path it came through — web app or Claude — with the
   not-recorded ones counted rather than hidden. The full activity read
   (intake-ui-parity.md) is still deferred; the page says so. */
import { useEffect, useState, type JSX } from "react";
import type { CapabilityFiling } from "../../../shared/capabilities";
import type { CanonicalTeam } from "../../../shared/canonical-teams";
import { ENTRY_PATH_LABEL, ENTRY_PATHS, type EntryPath } from "../../../shared/organizer-roles";
import { getFilings } from "./api";

type PathKey = EntryPath | "not-recorded";
const pathOf = (f: CapabilityFiling): PathKey => f.recorded.via ?? "not-recorded";
const LABEL: Record<PathKey, string> = { ...ENTRY_PATH_LABEL, "not-recorded": "Not recorded" };
const HELP: Record<PathKey, string> = {
  web: "Filed in the Hermes web app.",
  claude: "Filed by asking Claude, through Friday's connector.",
  "not-recorded": "Filed before Hermes recorded the path, or migrated. Never guessed.",
};

export function AuditLog({ teams }: { teams: CanonicalTeam[] | null }): JSX.Element {
  const [filings, setFilings] = useState<CapabilityFiling[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    getFilings()
      .then(setFilings)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);
  const teamName = (id: string): string => teams?.find((t) => t.id === id)?.name ?? id;
  const keys: PathKey[] = [...ENTRY_PATHS, "not-recorded"];

  return (
    <div data-intake-audit>
      <div className="page-head">
        <h1>Audit log</h1>
        <p>
          Every effort filed, and the path it came through — the <b>web app</b> or <b>Claude</b>.
          Edits, grooming and deletions are not shown here yet.
        </p>
      </div>
      {error !== null && <p className="capture-error">{error}</p>}
      {filings !== null && (
        <>
          <div className="stats">
            {keys.map((k) => (
              <div key={k} className="stat" title={HELP[k]} data-audit-count={k}>
                <div className="k">{LABEL[k]}</div>
                <div className="v">{filings.filter((f) => pathOf(f) === k).length}</div>
              </div>
            ))}
          </div>
          {filings.length === 0 ? (
            <p className="area-note">No efforts have been filed yet.</p>
          ) : (
            <div className="card">
              <div className="card-head">
                <h3>Filings</h3>
                <span className="count">{filings.length} efforts · newest first</span>
              </div>
              <div className="card-body" style={{ padding: 0 }}>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Filed</th>
                        <th>Effort</th>
                        <th>Team</th>
                        <th>Filed by</th>
                        <th title="How the request reached Hermes, read from the sign-in it carried.">Path</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filings.map((f) => (
                        <tr key={f.capabilityId}>
                          <td>{f.recorded.date.slice(0, 10)}</td>
                          <td>
                            {f.name}
                            {f.archived && <span className="cell-dash"> · archived</span>}
                          </td>
                          <td>{teamName(f.teamId)}</td>
                          <td>{f.recorded.by?.name ?? f.recorded.source}</td>
                          <td title={HELP[pathOf(f)]}>{LABEL[pathOf(f)]}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
